// FILE: src/pages/EnrolmentEndedPage.jsx
// Shown instead of course pages once the one-year enrolment has ended
// (MTO 6.3). The student stays signed in so support, profile and activity
// remain reachable; only an admin can extend the enrolment.
// Contacts are de-duplicated (useSupportChannels): channels that share a
// phone number show it once.
import { Box, Button, Card, CardContent, Stack, Typography } from "@mui/material";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import { Link as RouterLink } from "react-router-dom";
import useSupportChannels from "../hooks/useSupportChannels";
import SupportContactList from "../components/SupportContactList";
import { PageHeader, Section } from "../components/ui";

const POINTS = [
  { icon: LockOutlinedIcon, text: "Lessons and homelinks are closed, so no more course time can be recorded." },
  { icon: CheckCircleOutlineIcon, text: "Your records are kept: your progress, hours and test results stay on file." },
  { icon: SupportAgentOutlinedIcon, text: "Support, My profile and My activity are still open to you." }
];

export default function EnrolmentEndedPage() {
  const channels = useSupportChannels(true);
  return (
    <Box sx={{ maxWidth: 760, mx: "auto" }} data-testid="enrolment-ended">
      <PageHeader
        eyebrow="Course closed"
        title="Your enrolment has ended"
        description="Ontario rules give you one year from registration to finish the online course. That year is over, so lessons are closed."
      />

      <Stack spacing={2}>
        <Card sx={{ bgcolor: "zun.warningTint", borderColor: "transparent" }}>
          <CardContent>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1.5 }}>
              <EventBusyOutlinedIcon aria-hidden sx={{ color: "warning.main" }} />
              <Typography component="h2" variant="h3">
                What this means
              </Typography>
            </Stack>
            <Stack component="ul" spacing={1.25} sx={{ listStyle: "none", m: 0, p: 0 }}>
              {POINTS.map(({ icon: Icon, text }) => (
                <Stack component="li" key={text} direction="row" spacing={1.25} sx={{ alignItems: "flex-start" }}>
                  <Icon aria-hidden sx={{ color: "text.secondary", mt: "0.2rem", flexShrink: 0 }} />
                  <Typography>{text}</Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Section
          id="enrolment-support"
          title="Contact student support"
          description="If you think this is a mistake, or you need more time for a documented reason, student support can help. Only support can extend an enrolment."
        >
          <SupportContactList channels={channels} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2.5 }}>
            <Button component={RouterLink} to="/student/support/new" variant="contained">
              Ask for help
            </Button>
            <Button component={RouterLink} to="/student/support" variant="outlined">
              See my support requests
            </Button>
          </Stack>
        </Section>
      </Stack>
    </Box>
  );
}
